import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from "@angular/forms";
import moment from "moment";

/**
 * Validator function to check that at least one tag is present.
 * Validates both standard tags and custom tags.
 * 
 * @param {string} tagsControlName - The form control name for standard tags.
 * @param {string} customTagsControlName - The form control name for custom tags.
 * @returns {ValidatorFn} - A validator function to be used with Angular forms.
 */
export const atLeastOneTagValidator = (tagsControlName: string = 'tags', customTagsControlName: string = 'custom_tags'): ValidatorFn => {
    return (formGroup: UntypedFormGroup): ValidationErrors | null => {
        const tagsControl = formGroup.get(tagsControlName);
        const customTagsControl = formGroup.get(customTagsControlName);

        if (!tagsControl || !customTagsControl) {
            return null;
        }

        const tags = tagsControl.value;
        const customTags = customTagsControl.value;

        if (!tags && (!customTags || customTags.length === 0)) {
            return { atLeastOneTagRequired: true };
        }
        return null;
    };
}

/**
 * Validator function to ensure the end date is not before the start date.
 * 
 * @param {string} startDateControlName - The form control name for the start date.
 * @param {string} endDateControlName - The form control name for the end date.
 * @returns {ValidatorFn} - A validator function to be used with Angular forms.
 */
export const dateRangeValidator = (startDateControlName: string, endDateControlName: string): ValidatorFn => {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
        const startDateControl = formGroup.get(startDateControlName);
        const endDateControl = formGroup.get(endDateControlName);

        if (!startDateControl || !endDateControl) {
            return null;
        }

        const startDate: Date = startDateControl.value;
        const endDate: Date = endDateControl.value;

        if (startDate && endDate && moment(endDate).isBefore(moment(startDate))) {
            endDateControl.setErrors({ 'dateRangeInvalid': true });
            return { 'dateRangeInvalid': true };
        }

        return null;
    };
}

/**
 * Validator function to check if one time is before another.
 * @param startTimeFieldName The name of the start time field.
 * @param endTimeFieldName The name of the end time field.
 * @returns A ValidatorFn function.
 */
export const timeRangeValidator = (startTimeFieldName: string = 'startTime', endTimeFieldName: string = 'endTime'): ValidatorFn =>
    (group: AbstractControl): ValidationErrors | null => {
        const startTime = group.get(startTimeFieldName)?.value;
        const endTime = group.get(endTimeFieldName)?.value;

        if (!startTime || !endTime) {
            return null;
        }

        // Convert times to Moment objects for comparison
        const startMoment = moment(startTime, 'HH:mm');
        const endMoment = moment(endTime, 'HH:mm');

        // Check if end time is before start time
        if (endMoment.isBefore(startMoment)) {
            return { timeRange: true };
        }

        return null;
    };

export interface FormField<MetaType = any> {
    key: string;
    value: string | boolean | number;
    validators?: ValidatorFn[];
    meta?: MetaType;
}

export type FormFields = FormField[];

export type FormControls = Record<string, UntypedFormControl>;

/**
 * Creates a new FormControl with the given initial value and validators.
 * 
 * @param {FormField} field - The configuration used to create the FormControl.
 * @returns {FormControl} - The created FormControl.
 */
export const createFormControl = <MetaType = any>(field: FormField<MetaType>): UntypedFormControl => {
    return new UntypedFormControl(field.value ?? '', field.validators ?? []);
};

/**
 * Creates a dictionary of FormControls from an array of form field configurations.
 * 
 * @param {FormFields} fields - An array of form field configurations used to create FormControls.
 * @returns {FormControls} - An object where the keys are form control names and the values are FormControls.
 */
export const createFormControls = <MetaType = any>(fields: FormFields): FormControls => {
    return fields.reduce((controls: FormControls, field: FormField<MetaType>) => {
        controls[field.key] = createFormControl(field);
        return controls;
    }, {});
}


